import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = ["input", "previews", "hiddenFields", "purgeField"];
  static values = {
    maxFiles: { type: Number, default: 1 },
    maxSize: { type: Number, default: 5 },
    url: { type: String, default: "/rails/active_storage/direct_uploads" },
    fieldName: { type: String, default: "menu_item[photos][]" },
  };

  select() {
    const files = Array.from(this.inputTarget.files);

    const currentCount = this.previewsTarget.querySelectorAll(
      "[data-photo-preview]",
    ).length;
    if (currentCount + files.length > this.maxFilesValue) {
      if (this.maxFilesValue === 1) {
        this.#clearAll();
      } else {
        alert(`Maximum ${this.maxFilesValue} photos allowed`);
        return;
      }
    }

    files.forEach((file) => {
      if (file.size > this.maxSizeValue * 1024 * 1024) {
        alert(`${file.name} exceeds ${this.maxSizeValue}MB limit`);
        return;
      }
      this.#uploadFile(file);
    });

    this.inputTarget.value = "";
  }

  browse() {
    this.inputTarget.click();
  }

  remove(event) {
    const preview = event.target.closest("[data-photo-preview]");
    const img = preview.querySelector("img");
    if (img?.src?.startsWith("blob:")) URL.revokeObjectURL(img.src);

    const blobId = preview.dataset.blobId;
    if (blobId) {
      const hidden = this.hiddenFieldsTarget.querySelector(
        `input[value="${blobId}"]`,
      );
      hidden?.remove();
    }
    preview.remove();
    this.#updatePurgeField();
  }

  disconnect() {
    this.previewsTarget
      .querySelectorAll("[data-photo-preview] img")
      .forEach((img) => {
        if (img.src?.startsWith("blob:")) URL.revokeObjectURL(img.src);
      });
  }

  #clearAll() {
    this.previewsTarget
      .querySelectorAll("[data-photo-preview]")
      .forEach((el) => {
        const img = el.querySelector("img");
        if (img?.src?.startsWith("blob:")) URL.revokeObjectURL(img.src);
        el.remove();
      });
    this.hiddenFieldsTarget.innerHTML = "";
  }

  #uploadFile(file) {
    const preview = this.#createPreview(file);
    const progressBar = preview.querySelector("[data-progress]");

    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          const progress = (event.loaded / event.total) * 100;
          if (progressBar) progressBar.style.width = `${progress}%`;
        });
      },
    });

    upload.create((error, blob) => {
      if (error) {
        preview.remove();
        console.error("Upload failed:", error);
      } else {
        preview.dataset.blobId = blob.signed_id;
        progressBar?.parentElement?.remove();
        this.#addHiddenField(blob.signed_id);
      }
    });
  }

  #createPreview(file) {
    const container = document.createElement("div");
    container.dataset.photoPreview = "";
    container.className =
      "relative group rounded-xl overflow-hidden bg-base-300 aspect-square";

    const img = document.createElement("img");
    img.className = "w-full h-full object-cover";
    img.src = URL.createObjectURL(file);

    const progressWrap = document.createElement("div");
    progressWrap.className =
      "absolute bottom-0 left-0 right-0 h-1 bg-base-100/50";
    const progress = document.createElement("div");
    progress.dataset.progress = "";
    progress.className = "h-full bg-primary transition-all";
    progress.style.width = "0%";
    progressWrap.appendChild(progress);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className =
      "absolute top-2 right-2 w-6 h-6 rounded-full bg-error text-error-content " +
      "flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer";
    removeBtn.innerHTML =
      '<span class="material-symbols-outlined text-sm">close</span>';
    removeBtn.dataset.action = "click->photo-upload#remove";

    container.append(img, progressWrap, removeBtn);
    this.previewsTarget.appendChild(container);
    return container;
  }

  #addHiddenField(signedId) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.fieldNameValue;
    input.value = signedId;
    this.hiddenFieldsTarget.appendChild(input);
    this.#updatePurgeField();
  }

  #updatePurgeField() {
    if (!this.hasPurgeFieldTarget) return;

    const hasPhotos =
      this.previewsTarget.querySelectorAll("[data-photo-preview]").length > 0;
    this.purgeFieldTarget.value = hasPhotos ? "0" : "1";
  }
}
